import { useAsyncData, useRoute, useRuntimeConfig } from '#imports'

import type {
  MultipleQueriesQuery,
  SearchOptions,
} from '@algolia/client-search'
import { getModelsComparisonResults } from '@backmarket/http-api/src/api-specs-model-comparison'
import {
  type ModelComparisonCompareModels,
  type ModelComparisonModel,
} from '@backmarket/http-api/src/api-specs-model-comparison/models/compare-models'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import algoliasearch from 'algoliasearch'

import { isNotNullNorUndefined } from '~/utils/object/isNotNullNorUndefined'

import { type SelectedModels } from '../models/selected-models'
import { mapAlgoliaResponsesToModelCharacteristics } from '../utils/algolia/algoliaAdapter'
import type { ModelsComparisonAlgoliaResponse } from '../utils/algolia/comparable-model-algolia-hit'
import translations from '../utils/characteristics.translations'

import {
  type InitializableAlgoliaClient,
  initializeAlgoliaClientWith,
} from './configureAlgoliaSearch'

function replaceModelNameInFilters<ExtraParams extends object>({
  params,
  model,
}: {
  model: { catalogName: string; compareName: string }
  params: {
    facetFilters: (string | string[])[]
  } & ExtraParams
}) {
  const fixedFacetFilters = params.facetFilters.map((filter) =>
    Array.isArray(filter)
      ? filter
      : filter
          .replace('CATALOG_NAME', model.catalogName)
          .replace('COMPARE_NAME', model.compareName),
  )

  return {
    ...params,
    facetFilters: fixedFacetFilters,
  }
}

const replaceModelNameInFiltersTransformer =
  (model: ModelComparisonModel) => (queries: MultipleQueriesQuery[]) => {
    return queries.map((q) => {
      const paramsWithModelName = replaceModelNameInFilters({
        model,
        params: q.params as SearchOptions & {
          facetFilters: (string | string[])[]
        },
      })

      return {
        indexName: q.indexName,
        params: paramsWithModelName,
      }
    })
  }

function getAlgoliaDataForModel({
  algoliaId,
  algoliaClient,
  model,
  algoliaConfig,
}: {
  algoliaId: string
  algoliaClient: ReturnType<
    typeof initializeAlgoliaClientWith<ModelsComparisonAlgoliaResponse>
  >
  model: ModelComparisonModel
  algoliaConfig: ModelComparisonCompareModels['algoliaConfig']
}) {
  const includeModelInFilter = replaceModelNameInFiltersTransformer(model)

  const comparison = algoliaConfig.comparison || []

  return Promise.all(
    comparison?.map(async ({ apiKey, queries }) => {
      const algoliaSearch = algoliaClient(
        algoliaId,
        apiKey,
      )(includeModelInFilter)

      const result = await algoliaSearch(queries)

      return result
    }),
  )
}
const getModelsComparisonAlgoliaData =
  (
    initializableAlgoliaClient: InitializableAlgoliaClient<ModelsComparisonAlgoliaResponse>,
  ) =>
  (algoliaId: string) =>
  (modelsComparison?: ModelComparisonCompareModels) => {
    if (!modelsComparison) return null

    const { algoliaConfig, models } = modelsComparison

    if (!algoliaConfig?.comparison) return null

    const algoliaClient = initializeAlgoliaClientWith(
      initializableAlgoliaClient,
    )

    return Promise.all(
      models.map(async (model) => {
        if (!algoliaConfig.comparison) return null
        const algoliaData = await getAlgoliaDataForModel({
          algoliaConfig,
          algoliaId,
          algoliaClient,
          model,
        })

        return {
          algoliaData,
          model,
        }
      }),
    )
  }

function useGetModelsSlugsFromRoute() {
  const route = useRoute()
  const { productA, productB } = route.params as {
    productA: string
    productB: string
  }
  const modelsSlugs = [productA, productB]

  return { modelsSlugs }
}

export function useFetchModelsComparisonWithCustomClient(
  initializeAlgoliaClient: InitializableAlgoliaClient<ModelsComparisonAlgoliaResponse>,
) {
  return useAsyncData(async () => {
    const { modelsSlugs } = useGetModelsSlugsFromRoute()

    const locale = useI18nLocale()
    const i18n = useI18n()

    const { ALGOLIA_ID } = useRuntimeConfig().public

    const comparisonResults = await $httpFetch(getModelsComparisonResults, {
      pathParams: {
        model1: modelsSlugs[0],
        model2: modelsSlugs[1],
      },
    })

    const modelsData = await getModelsComparisonAlgoliaData(
      initializeAlgoliaClient,
    )(ALGOLIA_ID)(comparisonResults)

    const modelsNames = comparisonResults.models.map(
      (model) => model.compareName,
    ) as SelectedModels

    const characteristicsOfEachModel = modelsData
      ? modelsData.filter(isNotNullNorUndefined).map(({ algoliaData }) => {
          return mapAlgoliaResponsesToModelCharacteristics(algoliaData, {
            i18n,
            translations,
            locale,
          })
        })
      : []

    const comparison = {
      category: comparisonResults.category,
      content: comparisonResults.content,
      algoliaConfig: comparisonResults.algoliaConfig,
      link: comparisonResults.link,
      comparison: {
        modelsNames,
        characteristics: characteristicsOfEachModel,
      },
    }

    return comparison
  })
}

export function useFetchModelsComparison() {
  return useFetchModelsComparisonWithCustomClient(
    algoliasearch as InitializableAlgoliaClient<ModelsComparisonAlgoliaResponse>,
  )
}
